import type { Brand } from '@snapchat/mw-common';
import { useContext } from 'react';

import { AppContext } from '../AppContext';
import { Config } from '../config';
import type { BrandPropOverrides, SiteConfiguration } from '../configTypes';
import { UrlParameter } from '../constants/urlParameters';

type BrandPropOverrideKey = keyof BrandPropOverrides;

type BrandPropOverrideValue<K extends BrandPropOverrideKey> = BrandPropOverrides[K];

/**
 * Hook to get alternate brand prop overrides based on URL query parameters.
 *
 * Checks if the `alternateMotif` query parameter has a truthy value that matches a brand key in the
 * site configuration's `alternateBrandPropOverrides`. If found, returns the override value for the
 * specified key.
 *
 * @param siteConfig - The site configuration object
 * @param overrideKey - The key from BrandPropOverrides to retrieve (e.g., 'globalNavProps',
 *   'footerPropsOverride')
 * @returns The override value if found, otherwise undefined
 */
export function useAlternateBrandPropOverride<K extends BrandPropOverrideKey>(
  siteConfig: SiteConfiguration,
  overrideKey: K
): BrandPropOverrideValue<K> | undefined {
  const { getCurrentUrl } = useContext(AppContext);

  if (Config.isDeploymentTypeProd) {
    return undefined;
  }

  const url = new URL(getCurrentUrl());
  const alternateMotifValue = url.searchParams.get(UrlParameter.USE_ALTERNATE_MOTIF);

  if (!alternateMotifValue) {
    return undefined;
  }

  const alternateBrandOverrides = siteConfig.alternateBrandPropOverrides;

  if (!alternateBrandOverrides) {
    return undefined;
  }

  // Check if the query param value matches a Brand key in alternateBrandPropOverrides
  const brandOverrides = alternateBrandOverrides[alternateMotifValue as Brand];

  if (!brandOverrides) {
    return undefined;
  }

  return brandOverrides[overrideKey];
}
